import {
    QueryClient,
    useQuery,
    useMutation,
    type UseMutationOptions,
    type UseQueryOptions,
    type QueryKey,
    type FetchQueryOptions,
    QueryCache,
    isServer,
} from "@tanstack/react-query";
import { Logger, type LogTag, type ServiceType } from "@lib/monitoring/logger";

import type { AxiosRequestConfig } from "axios";
import { FetchServiceAxios } from "./fetchServiceAxios";

// Types
export type ApiError = {
    error: string;
    name?: string;
    stack?: string;
    status: number;
    message?: string;
};

export type QueryOnlyConfig<TQueryFnData, TData> = Omit<
    UseQueryOptions<TQueryFnData, ApiError, TData>,
    "queryKey" | "queryFn" | "select"
>;

export type QueryConfig<TQueryFnData, TData = TQueryFnData> = QueryOnlyConfig<
    TQueryFnData,
    TData
> & {
    axiosConfig?: AxiosRequestConfig;
    select?: (data: TQueryFnData) => TData;
};

export type MutationConfig<TData, TVariables> = Omit<
    UseMutationOptions<TData, ApiError, TVariables>,
    "mutationFn"
> & {
    axiosConfig?: AxiosRequestConfig;
    method?: "POST" | "PUT" | "DELETE";
};

export const useApi = {
    // Async function wrapper for React Query.
    /**
     *
     * @param queryKey Key of the request, useful to invalidate when need fresh data
     * @param queryFunction async function to be executed
     * @param config query
     */
    useAsync: <TQueryFnData, TData = TQueryFnData>(
        queryKey: QueryKey,
        queryFunction: UseQueryOptions<TQueryFnData, ApiError, TData>["queryFn"],
        config: QueryConfig<TQueryFnData, TData> = {}
    ) => {
        return useQuery<TQueryFnData, ApiError, TData>({
            queryKey,
            queryFn: queryFunction,
            ...config,
        });
    },

    // Mutation Wrapper
    // POST, PUT or DELETE wrapper. We are using "useMutate" to avoid confusion with...
    // ... useMutation from react-query Library
    /**
     *
     * @param url to the make the request, default is POST
     * @param options axios config or mutationConfig, if needed specify method, onSuccess handler, onError handlers, etc
     */
    useMutate: <TRequest, TResponse>(url: string, config?: MutationConfig<TResponse, TRequest>) => {
        const { axiosConfig, method = "POST", ...mutationConfig } = config || {};

        return useMutation<TResponse, ApiError, TRequest>({
            mutationFn: async (variables) => {
                try {
                    switch (method) {
                        case "PUT":
                            return await FetchServiceAxios.put<TResponse>(
                                url,
                                variables,
                                axiosConfig
                            );
                        case "DELETE":
                            return await FetchServiceAxios.delete<TResponse>(
                                url,
                                variables,
                                axiosConfig
                            );
                        case "POST":
                        default:
                            return await FetchServiceAxios.post<TRequest, TResponse>(
                                url,
                                variables,
                                axiosConfig
                            );
                    }
                } catch (error) {
                    return Promise.reject(error);
                }
            },
            ...mutationConfig,
        });
    },

    // Prefetch Wrapper
    /**
     *
     * @param queryClient We need a new instance of queryClient to avoid conflicts with the current instance
     * @param queryKey Key of the request, useful to invalidate when need fresh data or hydrate on the client
     * @param queryFunction async function to be executed
     * @param options axios config or queryConfig
     */
    usePrefetch: async <TData>(
        queryClient: QueryClient,
        queryKey: QueryKey,
        queryFunction: () => Promise<TData>,
        options?: FetchQueryOptions<TData>
    ) => {
        await queryClient.prefetchQuery({
            queryKey,
            queryFn: async () => await queryFunction(),
            ...options,
        });

        return queryClient;
    },
};

/**
 * @description Generates a new instance of QueryClient with a fresh cache and deafult options.
 * This should only be used when you specifically don't want other parts of the application to access the cache.
 * For general purpose you should use the getQueryClient instead.
 * @returns An instance of QueryClient
 */
const createQueryClient = () => {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60 * 1000 * 5, // 5 minutes
                retry: 3,
                // If retryOnMount is enabled, re-renders from errors can cause a feedback loop causing endless fetching because the component re-mounts.
                retryOnMount: false,
                refetchOnWindowFocus: false,
            },
        },
        queryCache: new QueryCache({
            onError: (error, query) => {
                if (!query.meta) {
                    //eslint-disable-next-line no-console
                    return console.error("Query error without Logger: ", error, query);
                }
                // TODO: Add types to the logger - re visit how we are passing the logger
                Logger.error(query.meta.serviceType as ServiceType, query.meta.message as string, {
                    tag: query.meta.tag as LogTag,
                    ...(query.meta.payload as Record<string, any>),
                    error: {
                        ...error,
                    },
                });
            },
        }),
    });
};

/**
 * @description Returns a QueryClient instance for the current environment.
 * If the code is running on the server, it will create a new instance of QueryClient.
 * If the code is running in the browser, it will return a cached instance of QueryClient.
 * This is useful to avoid creating multiple instances of QueryClient in the browser, which can lead
 * to performance issues and memory leaks.
 * @returns An instance of QueryClient
 */

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
    if (isServer) {
        return createQueryClient();
    } else {
        if (!browserQueryClient) {
            browserQueryClient = createQueryClient();
        }
        return browserQueryClient;
    }
}
